import React, { ReactNode, useEffect, useRef } from "react";
import "./index.scss";
import SvgIcon from "../../assets/icons/SvgIcon";
import { useNavigate } from "react-router-dom";
import { isTouchDevice } from "../../utils";

export interface MenuItem {
  key: string;
  value: string | ReactNode;
  iconName?: string;
  iconClassName?: string;
  url?: string;
  [key: string]: any;
}

interface MenuDropDownProps {
  heading: React.ReactNode;
  className?: string;
  menuItems?: MenuItem[];
  handleClick?: (item: MenuItem) => void;
  position?: "top" | "bottom";
  hasDivider?: boolean;
  children?: React.ReactNode;
  iconHide?: boolean;
  disableBackgroundScoll?: boolean;
  dropdownClassName?: string;
}

const MenuDropDown: React.FC<MenuDropDownProps> = ({
  heading,
  className = "",
  dropdownClassName = "",
  menuItems = [],
  handleClick,
  position = "bottom",
  hasDivider = false,
  children,
  iconHide = false,
  disableBackgroundScoll = false,
}) => {
  const dropDownRef = useRef<HTMLDetailsElement>(null);

  const navigate = useNavigate();

  useEffect(() => {
    function checkOutside(e: MouseEvent) {
      const element = dropDownRef.current;
      if (element && !element.contains(e.target as Node)) {
        element.removeAttribute("open");
      }
    }
    document.addEventListener("click", checkOutside);

    return () => {
      document.removeEventListener("click", checkOutside);
    };
  }, []);

  return (
    <div
      className={`menu-dropdown-container ${className} ${iconHide ? "icon-hide" : ""}`}
    >
      <details className="custom-dropdown" ref={dropDownRef}>
        <summary
          onClick={(e) => {
            e.stopPropagation();

            // Close all other dropdowns
            document
              .querySelectorAll("details.custom-dropdown[open]")
              .forEach((el) => {
                if (el !== dropDownRef.current) {
                  el.removeAttribute("open");
                }
              });
          }}
        >
          {heading}
        </summary>
        <div
          className={`dropdown-items mt-3 ${dropdownClassName} ${
            position === "top" ? "upside" : "downside"
          }`}
          onMouseEnter={() => {
            if (isTouchDevice()) {
              return;
            }
            if (disableBackgroundScoll) {
              const html = document.documentElement;
              const scrollTop = document.documentElement.scrollTop;

              // Save the current scroll position in a CSS variable
              html.style.setProperty("--st", `-${scrollTop}px`);
              // Freeze the scroll
              html.classList.add("noscroll");
              // Apply the scroll position as a transform to keep the user at the same scroll position visually
              html.style.top = `-${scrollTop}px`;
            }
          }}
          onMouseLeave={() => {
            if (disableBackgroundScoll) {
              const html = document.documentElement;

              // Restore the scroll position
              const scrollTop = Math.abs(parseInt(html.style.top || "0", 10));
              html.style.removeProperty("--st");
              html.classList.remove("noscroll");
              html.style.removeProperty("top");

              // Restore the actual scroll position
              window.scrollTo(0, scrollTop);
            }
          }}
        >
          {children ?? (
            <ul className="flex flex-col gap-1">
              {menuItems.map((item, index) => {
                return (
                  <React.Fragment key={`${item.key}-${index}`}>
                    <li
                      className={`dropdown-item dropdown-item-${index + 1} cursor-pointer hover:bg-slate-100`}
                    >
                      <button
                        type="button"
                        className="px-2 sm:px-4 py-1 sm:py-2 rounded w-full text-sm sm:text-base text-start"
                        onClick={(e) => {
                          e?.stopPropagation(); // Stop propagation to prevent Link clicks
                          e?.preventDefault(); // Prevent default action if URL is set
                          if (item?.url) {
                            navigate(item.url);
                          }
                          if (handleClick) {
                            handleClick(item);
                          }
                          if (dropDownRef.current) {
                            dropDownRef.current.open = false;
                          }
                        }}
                      >
                        {item.iconName ? (
                          <div className="flex items-center gap-1">
                            <span>
                              <SvgIcon
                                iconName={item.iconName}
                                className={item.iconClassName}
                              />
                            </span>
                            <span>{item.value}</span>
                          </div>
                        ) : (
                          item.value
                        )}
                      </button>
                    </li>
                    {menuItems.length - 2 >= index && hasDivider ? (
                      <hr />
                    ) : null}
                  </React.Fragment>
                );
              })}
            </ul>
          )}
        </div>
      </details>
    </div>
  );
};

export default MenuDropDown;
